import * as React from 'react';
import { BottomSheet, BottomSheetContent } from './BottomSheet';
import { Button, type ButtonProps } from './Button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './Dialog';
import { Inline } from './Layout';

export interface UrgentlyFormFrameProps {
  /**
   * `dialog` on the dashboard; `sheet` rises from the bottom edge on a
   * phone, as the new driver app's other forms do.
   */
  presentation: 'dialog' | 'sheet';
  open: boolean;
  /** Closed without submitting: the Cancel button, Escape or a swipe. */
  onCancel: () => void;
  title: string;
  /** The id of the form inside, which the submit button submits. */
  formId: string;
  submitLabel: string;
  submitVariant?: ButtonProps['variant'];
  isSubmitting: boolean;
  children: React.ReactNode;
}

/**
 * The frame the three Urgently forms (complete, GOA, cancel) share: a
 * dialog with Cancel and the submit along its foot, or the same as a bottom
 * sheet. The forms are the same on both; only the frame differs, so each
 * form draws itself once and the phone does not get a desktop dialog.
 */
export function UrgentlyFormFrame({
  presentation,
  open,
  onCancel,
  title,
  formId,
  submitLabel,
  submitVariant,
  isSubmitting,
  children,
}: UrgentlyFormFrameProps) {
  const onOpenChange = (next: boolean) => {
    if (!next) onCancel();
  };

  if (presentation === 'sheet') {
    return (
      <BottomSheet open={open} onOpenChange={onOpenChange}>
        <BottomSheetContent
          title={title}
          footer={
            <>
              <Button
                type="button"
                variant="secondary"
                size="xl"
                pill
                onClick={onCancel}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                form={formId}
                type="submit"
                variant={submitVariant}
                size="xl"
                pill
                loading={isSubmitting}
              >
                {submitLabel}
              </Button>
            </>
          }
        >
          {children}
        </BottomSheetContent>
      </BottomSheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="overflow-visible">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        {children}
        <DialogFooter>
          <Inline grow justify="between">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              form={formId}
              type="submit"
              disabled={isSubmitting}
              variant={submitVariant}
            >
              {submitLabel}
            </Button>
          </Inline>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
